import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { createSubmission } from '../../api/runs';
import { ApiError } from '../../api/client';
import ErrorAlert from '../../components/ErrorAlert';
import type { ChallengeFormProps } from './TextChallengeForm';

const QuizChallengeForm: React.FC<ChallengeFormProps> = ({
  orgSlug,
  runId,
  challenge,
  alreadySubmitted,
  onSubmitted,
  submitAction,
  initialContent,
}) => {
  const { t } = useTranslation('runs');
  const [selected, setSelected] = useState<string | null>(
    typeof initialContent?.selected_option === 'string' ? initialContent.selected_option : null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = (challenge.scoring_rules.options as string[] | undefined) ?? [];

  if (alreadySubmitted) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {t('participant.challenge.alreadySubmitted')}
      </Typography>
    );
  }

  if (options.length === 0) {
    return <ErrorAlert message={t('participant.challenge.quiz.noOptions')} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const content = { selected_option: selected };
      const result = submitAction
        ? await submitAction(content)
        : await createSubmission(orgSlug, runId, {
            run_challenge_id: challenge.id,
            content,
          });
      onSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && <ErrorAlert message={error} />}
        <FormControl component="fieldset" disabled={submitting}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            {t('participant.challenge.instructions')}
          </FormLabel>
          <RadioGroup
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value)}
          >
            {options.map((opt, idx) => (
              <FormControlLabel
                key={`${idx}-${opt}`}
                value={opt}
                control={<Radio />}
                label={opt}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          disabled={!selected || submitting}
        >
          {t('participant.challenge.submit')}
        </Button>
      </Stack>
    </Box>
  );
};

export default QuizChallengeForm;
